const PREDICTIVE_SEARCH_DEFAULT_COLLAPSED = 'predictive-search-default--collapsed';

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this._activeQueryId = 0;
    this.defaultSearch = this.querySelector('.default--search');
    this.demoSearch = this.querySelector('.wbsclose');
    this.searchActiveClass = this.dataset.searchActiveClass;
    this._scrollLocked = false;
    this._closePending = false;
    this._searchDismissed = false;

    /* This component is lazy-loaded on the first search interaction. Replay
       the interaction once the focused element has been upgraded: a typed
       query needs results, while an empty first focus needs the default
       suggestions panel. Without the empty-focus replay the first click only
       loads the component and the panel does not open until a second click. */
    queueMicrotask(() => {
      if (!this.isConnected || document.activeElement !== this.input) return;
      if (this.getQuery()) this.onChange();
      else this.onFocus();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this._events && !this._events.signal.aborted) return;
    this._events = new AbortController();
    const signal = this._events.signal;
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this), { signal });

    this.input.addEventListener('pointerdown', () => {
      this._prefocusScrollY = window.scrollY || window.pageYOffset || 0;
    }, { passive: true, signal });

    this.input.addEventListener('focus', this.onFocus.bind(this), { signal });
    this.input.addEventListener('input', () => { this._searchDismissed = false; }, { signal });
    this.addEventListener('focusout', this.onFocusOut.bind(this), { signal });
    this.addEventListener('keyup', this.onKeyup.bind(this), { signal });
    this.addEventListener('keydown', this.onKeydown.bind(this), { signal });
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.onResetButtonClick.bind(this), { signal });
    }
    const wbsClose = this.querySelector('.wbsclose');
    if (wbsClose) wbsClose.addEventListener('click', this.onClick.bind(this), { signal });

    this.boundOnDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', this.boundOnDocumentClick, { capture: true, signal });
    document.addEventListener('touchend', this.boundOnDocumentClick, { capture: true, signal });

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._events) this._events.abort();
    this._activeQueryId += 1;
    clearTimeout(this._focusOutTimer);
    clearTimeout(this._liveRegionTimer);
    cancelAnimationFrame(this._rowsFrame);
    this.releaseScrollRows();
    this.abortController.abort();
    this._unlockScroll();
    this.isOpen = false;
  }

  /* ── visibility helpers ── */

  _isVisible() {
    return this.isOpen || this.hasAttribute('loading') || !this.defaultSearch.classList.contains(PREDICTIVE_SEARCH_DEFAULT_COLLAPSED);
  }

  _dismissAll() {
    if (!this._isVisible()) return;
    this._searchDismissed = true;
    this._activeQueryId += 1;
    this.abortController.abort();
    this.removeAttribute('results');
    this.defaultSearch.classList.add(PREDICTIVE_SEARCH_DEFAULT_COLLAPSED);
    this.demoSearch.classList.add('hidden');
    this._unlockScroll();
    this.close();
  }

  /* ── event handlers ── */

  onDocumentClick(event) {
    if (!this._isVisible()) return;
    const clickedOverlay = event.target instanceof Element && event.target.closest('.predictive-search__panel-overlay');
    if (this.contains(event.target) && !clickedOverlay) return;
    this._dismissAll();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    // SearchForm debounces input events. A pending callback must not reopen
    // a panel dismissed with Escape or an outside click in the meantime.
    if (this._searchDismissed) return;
    const newSearchTerm = this.getQuery();

    if (!this.defaultSearch.classList.contains(PREDICTIVE_SEARCH_DEFAULT_COLLAPSED)) {
      this.defaultSearch.classList.add(PREDICTIVE_SEARCH_DEFAULT_COLLAPSED);
    }
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.defaultSearch.classList.remove(PREDICTIVE_SEARCH_DEFAULT_COLLAPSED);
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onResetButtonClick(event) {
    /* A reset button nested in the header's layered search markup could also
       reach the form submit path in some browsers. Handle it explicitly so
       clearing a query never navigates to /search. */
    event.preventDefault();
    event.stopPropagation();
    this.input.value = '';
    this.searchTerm = '';
    this._activeQueryId += 1;
    this.abortController.abort();
    this.abortController = new AbortController();
    this.toggleResetButton();
    this.predictiveSearchResults.innerHTML = '';
    this.removeAttribute('results');
    this.closeResults(true);
    this.defaultSearch.classList.remove(PREDICTIVE_SEARCH_DEFAULT_COLLAPSED);
    this.demoSearch.classList.remove('hidden');
    this._lockScroll();
    this.input.focus();
  }

  onFocus() {
    this._closePending = false;
    this._searchDismissed = false;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.defaultSearch.classList.remove(PREDICTIVE_SEARCH_DEFAULT_COLLAPSED);
      this.demoSearch.classList.remove('hidden');
      this._lockScroll();
      cancelAnimationFrame(this._rowsFrame);
      this._rowsFrame = requestAnimationFrame(() => {
        if (this.isConnected) this.bindScrollRowState();
      });
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this._lockScroll();
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (window.innerWidth < 768) return;

    this._closePending = true;
    clearTimeout(this._focusOutTimer);
    this._focusOutTimer = setTimeout(() => {
      if (!this._closePending) return;
      if (this.contains(document.activeElement)) return;
      this._dismissAll();
    }, 120);
  }

  onClick() {
    this._dismissAll();
  }

  onKeyup(event) {
    if (event.isComposing || event.keyCode === 229) return;
    if (!this.getQuery().length) this.close(true);
    if (this.hasAttribute('loading')) return;

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        if (this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')) {
          event.preventDefault();
          this.selectOption();
        }
        break;
    }
  }

  onKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;
    if (event.key === 'Enter' && this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button, button[aria-selected="true"]')) {
      event.preventDefault(); // Activate once on keyup, not via native submit too.
    }
    if (event.key === 'Escape' && this._isVisible()) {
      event.preventDefault();
      event.stopPropagation();
      this.input.focus({ preventScroll: true });
      this._dismissAll();
      return;
    }
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.textContent;
    if (!currentButtonText || !previousTerm) return;
    let safePattern;
    try {
      safePattern = new RegExp(previousTerm.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&'), 'g');
    } catch {
      return;
    }
    const matches = currentButtonText.match(safePattern);
    if (matches && matches.length > 1) return;
    searchForTextElement.textContent = currentButtonText.replace(safePattern, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll('[role="option"]')
    ).filter((element) => element.offsetParent !== null);
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = allVisibleElements.indexOf(selectedElement);

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement.id) {
      activeElement.id = `predictive-search-option-runtime-${activeElementIndex + 1}`;
    }
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
    // aria-activedescendant keeps focus in the input; unlike DOM focus it
    // does not reveal options clipped by a horizontal results rail.
    activeElement.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const url = window.StorefrontSearch.predictiveUrl(searchTerm);
    const context = JSON.stringify([window.Shopify?.country || '', window.Shopify?.currency?.active || '', document.documentElement?.lang || '']);
    const queryKey = context + url;
    const contextIsCurrent = () => context === JSON.stringify([window.Shopify?.country || '', window.Shopify?.currency?.active || '', document.documentElement?.lang || '']);
    if (!window.StorefrontSearch.predictiveSupported()) {
      this.renderErrorState(true);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const queryId = ++this._activeQueryId;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (queryId !== this._activeQueryId || this.getQuery() !== searchTerm || !contextIsCurrent()) return null;
        if (!response.ok) {
          this.renderErrorState(response.status === 417);
          return null;
        }
        return response.text();
      })
      .then((text) => {
        if (text == null) return;
        if (queryId !== this._activeQueryId || this.getQuery() !== searchTerm || !contextIsCurrent()) return;

        const doc = new DOMParser().parseFromString(text, 'text/html');
        const sectionRoot =
          doc.querySelector('#shopify-section-predictive-search') ||
          doc.querySelector('[id^="shopify-section-"]');
        if (!sectionRoot) {
          this.renderErrorState();
          return;
        }
        const resultsMarkup = sectionRoot.innerHTML;
        const keys = Object.keys(this.cachedResults);
        if (keys.length >= 20) delete this.cachedResults[keys[0]];
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup, searchTerm);
      })
      .catch((error) => {
        if (queryId !== this._activeQueryId || this.getQuery() !== searchTerm || !contextIsCurrent()) return;
        if (error.name === 'AbortError') {
          if (queryId === this._activeQueryId) this.removeAttribute('loading');
          return;
        }
        this.removeAttribute('loading');
        this.renderErrorState();
      });
  }

  renderErrorState(unsupported = false) {
    const errorText = (unsupported && window.storefrontSearchConfig.unavailable) || this.dataset.errorText || 'Search is temporarily unavailable. Please try again.';
    const retryText = this.dataset.retryText || 'Try again';
    const results = document.createElement('div');
    results.id = 'predictive-search-results';
    results.className = 'wbsearch_inner predictive-search__error';
    results.setAttribute('role', 'status');

    const message = document.createElement('p');
    message.className = 'predictive-search__empty-text';
    message.textContent = errorText;
    results.appendChild(message);

    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'predictive-search__retry button button--secondary';
    retry.textContent = retryText;
    retry.addEventListener('click', () => this.getSearchResults(this.getQuery()));
    if (!unsupported) results.appendChild(retry);
    const fullSearch = document.createElement('a');
    fullSearch.className = 'button button--secondary';
    fullSearch.href = window.StorefrontSearch.searchUrl(this.getQuery());
    fullSearch.textContent = window.storefrontSearchConfig.searchFor.replace('__TERMS__', this.getQuery());
    results.appendChild(fullSearch);

    this.releaseScrollRows();
    this.predictiveSearchResults.replaceChildren(results);
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.setLiveRegionText(errorText);
    this._lockScroll();
    this.open();
  }

  setLiveRegionLoadingState() {
    this.input.removeAttribute('aria-activedescendant');
    this.querySelectorAll('[aria-selected="true"]').forEach((option) => {
      option.setAttribute('aria-selected', 'false');
    });
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    clearTimeout(this._liveRegionTimer);
    this._liveRegionTimer = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm) {
    this.releaseScrollRows();
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    if (this.predictiveSearchResults.querySelector('.predictive-search__empty')) {
      this.close();
      this._unlockScroll();
      return;
    }
    if (window.localizePredictiveSearch) window.localizePredictiveSearch(this.predictiveSearchResults);
    const productResults = this.predictiveSearchResults.querySelector('.search_result-products');
    const articleResults = this.predictiveSearchResults.querySelector('.predictive-search__article-wrapper');
    const resultRightColumn = this.predictiveSearchResults.querySelector('.searchbar-right.wbsrightpart');
    if (productResults && articleResults && resultRightColumn) {
      resultRightColumn.appendChild(articleResults);
    }
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.setLiveRegionResults();
    this._lockScroll();
    this.open();
    this.bindScrollRowState();
    window.AlAsilDiscovery?.emit('search_suggest_viewed', {
      search_term: searchTerm,
      suggestions_count: this.predictiveSearchResults.querySelectorAll('[role="option"]:not(.predictive-search__item--term)').length,
    });
  }

  releaseScrollRows() {
    this._rowCleanups?.forEach(cleanup => cleanup());
    this._rowCleanups = [];
  }

  bindScrollRowState() {
    // The default-state rows (.products-scroll-row / .categories-scroll-row)
    // scroll horizontally with hidden scrollbars. Wire up: (1) edge fades that
    // hint there's more to scroll, (2) vertical mouse-wheel → horizontal scroll,
    // (3) click-and-drag scrolling. RTL-aware (Arabic uses inverted scrollLeft).
    const rows = this.querySelectorAll('.products-scroll-row, .categories-scroll-row');

    for (const row of rows) {
      if (!this.contains(row) || row.dataset.psScrollBound === '1') continue;
      row.dataset.psScrollBound = '1';
      const controller = new AbortController();
      const signal = controller.signal;

      const wrapper = row.closest('.products-scroll-wrapper, .categories-scroll-wrapper') || row.parentElement;
      const isRTL = getComputedStyle(row).direction === 'rtl';
      const maxScroll = () => row.scrollWidth - row.clientWidth;

      // ---- Edge fades (rAF-throttled so a fast scroll/drag doesn't thrash layout) ----
      let fadeRaf = null;
      const updateFades = () => {
        fadeRaf = null;
        const max = maxScroll();
        if (max <= 1) {
          wrapper.classList.remove('ps-fade-start', 'ps-fade-end');
        } else {
          // atStart = nothing hidden before the start edge; atEnd = nothing hidden after.
          const atStart = isRTL ? row.scrollLeft >= -1 : row.scrollLeft <= 1;
          const atEnd = isRTL ? row.scrollLeft <= -max + 1 : row.scrollLeft >= max - 1;
          wrapper.classList.toggle('ps-fade-start', !atStart);
          wrapper.classList.toggle('ps-fade-end', !atEnd);
        }

      };
      const scheduleFades = () => {
        if (fadeRaf === null) fadeRaf = requestAnimationFrame(updateFades);
      };
      row.addEventListener('scroll', scheduleFades, { passive: true, signal });
      updateFades();

      // ---- Mouse wheel → horizontal scroll ----
      row.addEventListener(
        'wheel',
        (e) => {
          if (maxScroll() <= 1) return;
          // Honour an explicit horizontal wheel (trackpads); otherwise map vertical to horizontal.
          const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
          if (delta === 0) return;
          e.preventDefault();
          row.scrollLeft += delta;
        },
        { passive: false, signal }
      );

      // ---- Click-and-drag scrolling ----
      // The cards contain <img>/<a>, which are natively draggable - pressing one
      // and moving starts the browser's drag-and-drop and cancels our pointer
      // stream. Cancel that native drag, and capture the pointer so we keep
      // getting moves even past the row's edges.
      row.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

      let dragging = false;
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      let pid = null;
      let bound = 0; // cached max scroll for the current gesture (no per-move layout reads)
      let target = 0;
      let moveRaf = null;

      const applyScroll = () => {
        moveRaf = null;
        row.scrollLeft = target;
      };
      const onPointerDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return; // left button only for mouse
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = row.scrollLeft;
        pid = e.pointerId;
        bound = maxScroll();
      };
      const onPointerMove = (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (!moved) {
          if (Math.abs(dx) <= 4) return; // tolerance so a plain click still works
          moved = true;
          row.classList.add('is-dragging'); // grabbing cursor + user-select:none (CSS)
          try {
            row.setPointerCapture(pid);
          } catch (_) {}
        }
        e.preventDefault(); // stop text/image selection while dragging
        // Clamp within bounds (LTR 0..max, RTL -max..0) and coalesce to one write per frame.
        const raw = startScroll - dx;
        target = isRTL ? Math.min(0, Math.max(-bound, raw)) : Math.max(0, Math.min(bound, raw));
        if (moveRaf === null) moveRaf = requestAnimationFrame(applyScroll);
      };
      const onPointerUp = () => {
        if (!dragging) return;
        dragging = false;
        row.classList.remove('is-dragging');
        if (moveRaf !== null) {
          cancelAnimationFrame(moveRaf);
          moveRaf = null;
          row.scrollLeft = target; // flush the final position; free scroll stays put
        }
        try {
          if (pid !== null) row.releasePointerCapture(pid);
        } catch (_) {}
        pid = null;
      };
      row.addEventListener('pointerdown', onPointerDown, { signal });
      row.addEventListener('pointermove', onPointerMove, { signal });
      row.addEventListener('pointerup', onPointerUp, { signal });
      row.addEventListener('pointercancel', onPointerUp, { signal });
      // Suppress the click that fires after a drag, so dragging doesn't open a product/category.
      row.addEventListener(
        'click',
        (e) => {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
            moved = false;
          }
        },
        { capture: true, signal }
      );
      (this._rowCleanups ||= []).push(() => {
        controller.abort();
        cancelAnimationFrame(fadeRaf);
        onPointerUp();
        delete row.dataset.psScrollBound;
      });
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    const msg = countEl?.textContent?.trim() || '';
    if (msg) this.setLiveRegionText(msg);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
  }

  /* ── scroll lock (single entry/exit) ── */

  _lockScroll() {
    if (this._scrollLocked || !this.searchActiveClass) return;
    this._scrollLocked = true;
    this._psearchLockScrollY = this._prefocusScrollY ?? window.scrollY ?? window.pageYOffset ?? 0;
    this._prefocusScrollY = null;
    document.documentElement.style.setProperty('--psearch-lock-top', `-${this._psearchLockScrollY}px`);
    document.documentElement.classList.add('psearch-open');
    document.body.classList.add('psearch-open');
    document.body.classList.add(this.searchActiveClass);
  }

  _unlockScroll() {
    if (!this._scrollLocked || !this.searchActiveClass) return;
    this._scrollLocked = false;
    const y = this._psearchLockScrollY ?? 0;
    document.body.classList.remove(this.searchActiveClass);
    document.body.classList.remove('psearch-open');
    document.documentElement.classList.remove('psearch-open');
    document.documentElement.style.removeProperty('--psearch-lock-top');
    this._psearchLockScrollY = null;
    this._prefocusScrollY = null;
    /* Temporarily block sticky-header hide so the scrollTo restore
       doesn't trigger the scroll handler to hide the header. */
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader) {
      stickyHeader.preventHide = true;
      stickyHeader.currentScrollTop = y;
    }

    window.scrollTo(0, y);

    const hdr = document.querySelector('.section-header');
    if (hdr) hdr.classList.remove('shopify-section-header-hidden');

    /* Release the guard after scroll events settle. */
    requestAnimationFrame(() => {
      if (stickyHeader) {
        stickyHeader.currentScrollTop = window.scrollY;
        stickyHeader.preventHide = false;
      }
    });
  }
}

customElements.define('predictive-search', PredictiveSearch);
